import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Download, ExternalLink } from "lucide-react";
import {
  compareDashboardValues,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSearch,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DefinitionTooltip,
  TablePagePagination,
  toggleTableSort,
  dashboardDataTable as dt,
  type SortDirection,
} from "src/components/common";
import { downloadCsv } from "src/dashboards/paidMedia/utils/paidMediaCsvExport";
import {
  formatNumber,
  formatNumberDigits,
  wrapBidiIsolate,
  stripBidiIsolateMarks,
} from "src/utils/numberFormat";
import type {
  OrganicSocialPlatform,
  SocialContentRow,
} from "../../../data/organicSocialMockData";
import {
  formatOrganicPostTypeLabel,
  getOrganicContentSectionDefinitionInfo,
  getOrganicPlatformMetricDefinitionInfo,
} from "../organicMediaDefinitions";
import PostMediaThumb from "../PostMediaThumb";
import {
  SOCIAL_CONTENT_COLUMNS,
  SOCIAL_CONTENT_PAGE_SIZE,
  SOCIAL_PLATFORM_LABEL_KEY,
  type SocialContentSortKey,
} from "./socialReportConfig";

const CAPTION_LIMIT = 48;

const truncateCaption = (caption: string, limit = CAPTION_LIMIT) =>
  caption.length > limit ? `${caption.slice(0, limit).trimEnd()}…` : caption;

interface SocialReportContentTableProps {
  platform: OrganicSocialPlatform;
  rows: SocialContentRow[];
  csvFilename: string;
}

const SocialReportContentTable: FC<SocialReportContentTableProps> = ({
  platform,
  rows,
  csvFilename,
}) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: SocialContentSortKey; direction: SortDirection }>({
    key: "impressions",
    direction: "desc",
  });

  const platformLabel = t(SOCIAL_PLATFORM_LABEL_KEY[platform]);

  const sortedRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? rows.filter(row => row.caption.toLowerCase().includes(needle))
      : rows;
    return [...filtered].sort((left, right) =>
      compareDashboardValues(left[sort.key], right[sort.key], sort.direction)
    );
  }, [rows, search, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / SOCIAL_CONTENT_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * SOCIAL_CONTENT_PAGE_SIZE,
    currentPage * SOCIAL_CONTENT_PAGE_SIZE
  );

  const title = t("organicMedia.social.sections.contentLevelPerformance");

  const formatCell = (row: SocialContentRow, column: (typeof SOCIAL_CONTENT_COLUMNS)[number]) => {
    const value = row[column.key];
    if (column.format === "percent") {
      return wrapBidiIsolate(
        `${formatNumber(Number(value ?? 0), { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`
      );
    }
    return formatNumber(Number(value ?? 0), {}, i18n.language);
  };

  const formatExportCell = (column: (typeof SOCIAL_CONTENT_COLUMNS)[number], row: SocialContentRow) => {
    const value = Number(row[column.key] ?? 0);
    if (column.format === "percent") {
      return stripBidiIsolateMarks(
        `${formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`
      );
    }
    return formatNumberDigits(value, {}, i18n.language);
  };

  const handleExport = () => {
    downloadCsv(
      csvFilename,
      [
        t("organicMedia.social.table.content"),
        t("organicMedia.social.contentType"),
        ...SOCIAL_CONTENT_COLUMNS.map(column => t(column.labelKey)),
        t("organicMedia.social.table.link"),
      ],
      sortedRows.map(row => [
        row.caption,
        formatOrganicPostTypeLabel(row.typeKey, t),
        ...SOCIAL_CONTENT_COLUMNS.map(column => formatExportCell(column, row)),
        row.link,
      ])
    );
  };

  return (
    <div className="organic-content-report__post-table-block">
      <div className="organic-post-details organic-post-details--modern">
        <DashboardDataTablePanel>
          <DashboardDataTablePanelHeader>
            <div className="organic-content-panel__title">
              <h3>{title}</h3>
              <DefinitionTooltip
                label={title}
                info={getOrganicContentSectionDefinitionInfo("postLevelPerformance", t)}
                formulaTitle={t("organicMedia.common.formula")}
              />
            </div>
            <div className="organic-post-details__table-toolbar">
              <DashboardDataTableSearch
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder={t("organicMedia.social.searchContent")}
              />
              <button
                type="button"
                className="organic-post-details__export-button"
                onClick={handleExport}
                disabled={sortedRows.length === 0}
              >
                <Download size={14} aria-hidden />
                {t("organicMedia.contentReport.exportCsv")}
              </button>
            </div>
          </DashboardDataTablePanelHeader>

          <DashboardDataTableScroll className="organic-post-details__table-wrap">
            <DashboardDataTableTable className="organic-post-details__table organic-post-details__table--modern">
              <colgroup>
                <col className="organic-post-details__col-post" />
                {SOCIAL_CONTENT_COLUMNS.map(column => (
                  <col key={column.key} className="organic-post-details__col-metric" />
                ))}
                <col className="organic-post-details__col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th className="organic-post-details__th-post">
                    <DashboardDataTableSortButton
                      onClick={() => setSort(current => toggleTableSort(current, "caption"))}
                    >
                      {t("organicMedia.social.table.content")?.toUpperCase()}
                    </DashboardDataTableSortButton>
                  </th>
                  {SOCIAL_CONTENT_COLUMNS.map((column, index) => (
                    <th key={column.key} className="organic-post-details__th-tooltip">
                      <span className={dt.thHelpRow}>
                        <DashboardDataTableSortButton
                          onClick={() => setSort(current => toggleTableSort(current, column.key))}
                        >
                          {t(column.labelKey)?.toUpperCase()}
                        </DashboardDataTableSortButton>
                        <DefinitionTooltip
                          label={t(column.labelKey)}
                          info={getOrganicPlatformMetricDefinitionInfo(
                            platform,
                            column.helpLeaf,
                            t(column.labelKey),
                            t
                          )}
                          align={index === SOCIAL_CONTENT_COLUMNS.length - 1 ? "end" : "start"}
                          formulaTitle={t("organicMedia.common.formula")}
                        />
                      </span>
                    </th>
                  ))}
                  <th>{t("organicMedia.social.table.link")?.toUpperCase()}</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(row => (
                  <tr key={row.id}>
                    <td>
                      <div className="organic-post-details__post-cell">
                        <div className="organic-post-details__thumb" aria-hidden>
                          <PostMediaThumb
                            thumbnailUrl={row.thumbnailUrl}
                            platform={platform}
                          />
                        </div>
                        <div className="organic-post-details__post-copy">
                          <div className="organic-post-details__caption-wrap">
                            <strong>{truncateCaption(row.caption)}</strong>
                            <div className="organic-post-details__caption-tooltip">
                              {row.caption}
                            </div>
                          </div>
                          <div className="organic-post-details__tag-row">
                            <span className="organic-post-details__mini-tag organic-post-details__mini-tag--platform">
                              {platformLabel}
                            </span>
                            <span className="organic-post-details__mini-tag organic-post-details__mini-tag--type">
                              {formatOrganicPostTypeLabel(row.typeKey, t)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    {SOCIAL_CONTENT_COLUMNS.map(column => (
                      <td key={column.key}>{formatCell(row, column)}</td>
                    ))}
                    <td className="organic-post-details__action-cell">
                      <a
                        className="organic-post-details__action-button"
                        href={row.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink size={14} aria-hidden />
                        {t("organicMedia.social.table.viewContent")}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DashboardDataTableTable>
          </DashboardDataTableScroll>

          <TablePagePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPrevious={() => setPage(current => Math.max(1, current - 1))}
            onNext={() => setPage(current => Math.min(totalPages, current + 1))}
            className="organic-post-details__pager"
          />
        </DashboardDataTablePanel>
      </div>
    </div>
  );
};

export default SocialReportContentTable;
